import React, { ReactNode } from "react";
import LineChart from "common/Charts/LineChart/LineChart";
import BarChart from "common/Charts/BarChart";
import { FormattedChartDataPoint, TimeRange } from "@shared/types/common";
import ChartCardLayout from "common/Layouts/ChartCardLayout";
import { BotData, BotGraphType, Timeframe } from "@shared/types";
import {
  BASE_CHART_TIME_RANGES,
  TIME_RANGE_START,
} from "@shared/common/constants";
import NewLookBotChart from "features/bots/NewLook/NewLookBotChart";
import { useBotChart } from "@shared/features/bots/hooks";
import { useTheme } from "styled-components";

interface BotChartProps {
  botId?: number;
  publicBotUUID?: string;
  chartType: BotGraphType;
  botData?: BotData;
  title?: ReactNode;
  mobileBreakpoint?: number;
  defaultTimeRange?: TimeRange;
  isBacktesting?: boolean;
  hideTimeRanges?: boolean;
  liveChartData?: {
    data?: FormattedChartDataPoint[];
    timeframe?: Timeframe;
    currentJobTimestamp?: number;
  };
  isDemoCopyPublic?: boolean;
}

const BotChart = ({
  botId,
  publicBotUUID,
  chartType,
  botData,
  title,
  mobileBreakpoint = 480,
  defaultTimeRange,
  isBacktesting,
  hideTimeRanges,
  liveChartData,
  isDemoCopyPublic,
}: BotChartProps) => {
  const {
    graphData,
    displayCurrency,
    isFetching,
    isPercent,
    isSuccess,
    chartPrecision,
    timeRange,
    setTimeRange,
    isLineChart,
    strXMinMax,
  } = useBotChart({
    botId,
    publicBotUUID,
    chartType,
    botData,
    defaultTimeRange,
    isBacktesting,
    liveChartData,
    isDemoCopyPublic,
  });

  const { isNewLook } = useTheme();

  const chart = isLineChart ? (
    <LineChart
      data={graphData}
      loading={isFetching}
      displayCurrency={isPercent ? undefined : displayCurrency}
      precision={chartPrecision}
      isSuccess={isSuccess}
      xMax={strXMinMax?.max}
      xMin={strXMinMax?.min}
    />
  ) : (
    <BarChart
      data={graphData}
      loading={isFetching}
      displayCurrency={isPercent ? undefined : displayCurrency}
      usePositiveNegativeColorScheme={true}
      precision={chartPrecision}
      isSuccess={isSuccess}
      xMax={strXMinMax?.max}
      xMin={strXMinMax?.min}
    />
  );

  if (isNewLook) {
    return (
      <NewLookBotChart
        graphData={graphData}
        isFetching={isFetching}
        isPercent={isPercent}
        isLineChart={isLineChart}
        displayCurrency={displayCurrency}
        precision={chartPrecision}
        timeRange={timeRange}
        setTimeRange={hideTimeRanges ? undefined : setTimeRange}
        title={title}
        timeframe={liveChartData?.timeframe}
      />
    );
  }

  return (
    <ChartCardLayout
      change={title}
      chart={chart}
      selectedTimeRange={timeRange}
      setTimeRange={setTimeRange}
      buttonSize="medium"
      mobileBreakpoint={mobileBreakpoint}
      autoLayoutHeight={true}
      timeRanges={[TIME_RANGE_START, ...BASE_CHART_TIME_RANGES]}
      hideTimeRanges={hideTimeRanges}
    />
  );
};

export default BotChart;
